/**
 * The plan body as a document, the way Cursor's plan editor renders a plan's
 * markdown: 14px body, document-scale headings, real bullets, inline code
 * for paths. Shares the chat prose's list, link and code handling; only the
 * scale differs.
 */
import { memo, type ComponentProps, type FC } from "react";
import { Streamdown } from "streamdown";

import { PROSE_COMPONENTS } from "@/features/agents/refs";
import { cn } from "@/lib/utils";

type Components = NonNullable<ComponentProps<typeof Streamdown>["components"]>;
type HeadingProps = ComponentProps<"h1"> & { node?: unknown };

const heading = (
  Tag: "h1" | "h2" | "h3" | "h4",
  size: string
): FC<HeadingProps> => {
  const Heading: FC<HeadingProps> = ({ className, node: _n, ...rest }) => (
    <Tag
      className={cn(
        "mt-7 mb-2 font-semibold tracking-tight text-foreground first:mt-0",
        size,
        className
      )}
      {...rest}
    />
  );
  Heading.displayName = `PlanProse${Tag.toUpperCase()}`;
  return Heading;
};

const DOC_COMPONENTS = {
  ...(PROSE_COMPONENTS as Record<string, unknown>),
  h1: heading("h1", "text-xl"),
  h2: heading("h2", "text-lg"),
  h3: heading("h3", "text-md"),
  h4: heading("h4", "text-md")
} as unknown as Components;

type Props = {
  children: string;
  className?: string;
};

const PlanProseView: FC<Props> = ({ children, className }) => (
  <div
    className={cn(
      "plan-prose min-w-0 break-words text-md leading-[1.7] text-fg",
      "[&_code:not(pre_code)]:rounded [&_code:not(pre_code)]:bg-muted [&_code:not(pre_code)]:px-1 [&_code:not(pre_code)]:py-px [&_code:not(pre_code)]:font-mono [&_code:not(pre_code)]:text-[0.88em]",
      "[&_ul]:my-2.5 [&_ol]:my-2.5 [&_li]:my-1 [&_p]:my-2.5 [&_table]:text-base",
      className
    )}
    data-slot="plan-prose"
    data-testid="plan-markdown"
  >
    <Streamdown
      mode="static"
      components={DOC_COMPONENTS}
      lineNumbers={false}
      className="size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
    >
      {children}
    </Streamdown>
  </div>
);

export const PlanProse = memo(PlanProseView);
PlanProse.displayName = "PlanProse";
